/**
 * Types for timeline data structures
 */
interface TimeMarker {
  time: number; // Timestamp in milliseconds
  percent: number; // Position as percentage of day (0-100)
  isMajor: boolean; // Whether this is a major (labeled) marker
}

interface MarkerSpecs {
  major: number; // Interval in seconds for major markers
  minor: number; // Interval in seconds for minor markers
  format: string; // Time format to display
}

interface ViewWindow {
  start: number; // Start position as percentage of day (0-100)
  end: number; // End position as percentage of day (0-100)
}

/**
 * Constants for time calculations
 */
const SECONDS_IN_DAY = 24 * 60 * 60; // 86400 seconds
const MIN_INTERVAL_SECONDS = 60; // 1 minute
const MAX_INTERVAL_SECONDS = 10 * 60; // 10 minutes

// Percentage of day
const MIN_INTERVAL_PERCENT = (MIN_INTERVAL_SECONDS / SECONDS_IN_DAY) * 100;
const MAX_INTERVAL_PERCENT = (MAX_INTERVAL_SECONDS / SECONDS_IN_DAY) * 100;

// Snap threshold
const SNAP_THRESHOLD = 0.01;

/**
 * Time formatters for different zoom levels
 */
const formatters = {
  // HH:MM:SS format
  time: new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }),

  // HH:MM format
  timeWithMinutes: new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }),

  // HH format
  timeWithHours: new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    hour12: false,
  }),
};

/**
 * Convert a percentage of day to a DateTime object
 *
 * @param percent - Position as percentage of day (0-100)
 * @param baseDate - Optional base date to use (defaults to today)
 * @returns Date object representing the time
 */
const percentToDateTime = (percent: number, baseDate?: Date): Date => {
  // Create date object from base or create new for today
  const date = baseDate ? new Date(baseDate) : new Date();

  // Calculate total seconds in day from percentage
  const totalSeconds = (percent / 100) * SECONDS_IN_DAY;

  // Reset to start of day and add seconds
  date.setHours(0, 0, 0, 0);
  date.setSeconds(Math.floor(totalSeconds));

  return date;
};

/**
 * Convert a DateTime object to a percentage of day
 *
 * @param date - Date object to convert
 * @returns Position as percentage of day (0-100)
 */
const dateTimeToPercent = (date: Date): number => {
  // Get time components
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Calculate total seconds elapsed in day
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  // Convert to percentage
  return (totalSeconds / SECONDS_IN_DAY) * 100;
};

/**
 * Calculate position within the current viewport based on global percentage
 *
 * @param timePercent - Global position as percentage of day (0-100)
 * @param viewWindow - Current viewport window (start/end percentages)
 * @returns Position as percentage within the current viewport (0-100)
 */
const getPositionInViewport = (timePercent: number, viewWindow: ViewWindow): number => {
  return ((timePercent - viewWindow.start) / (viewWindow.end - viewWindow.start)) * 100;
};

/**
 * Calculate the duration of a range in milliseconds, ensuring it doesn't exceed 10 minutes for non-timelapse
 *
 * @param startPercent - Start position as percentage of day (0-100)
 * @param endPercent - End position as percentage of day (0-100)
 * @param isTimelapse - Whether this is a timelapse request
 * @returns Duration in milliseconds, capped at 10 minutes for non-timelapse only
 */
const calculateDurationFromRange = (startPercent: number, endPercent: number, isTimelapse: boolean): number => {
  const durationMs = Math.round(Math.abs(((endPercent - startPercent) / 100) * SECONDS_IN_DAY * 1000));
  return isTimelapse ? durationMs : Math.min(durationMs, 10 * 60 * 1000);
};

/**
 * Calculate if a position should snap to a period edge
 *
 * @param position - Current position as percentage of day (0-100)
 * @param periods - Array of period objects containing trip start/end timestamps
 * @param snapThreshold - Threshold distance for snapping (percentage points)
 * @returns Snapped position or original position if no snap
 */
const snapToNearestPeriod = (
  position: number,
  periods: Array<{ trip: { startTimestamp: number; endTimestamp: number } }>,
  snapThreshold = 0.5 // Default threshold of 0.5%
): number => {
  if (!periods.length) return position;

  // Convert periods to percentage positions
  const snapPoints = periods.flatMap(({ trip }) => [dateTimeToPercent(new Date(trip.startTimestamp)), dateTimeToPercent(new Date(trip.endTimestamp))]);

  // Find the closest snap point
  let closestPoint = position;
  let minDistance = snapThreshold;

  for (const point of snapPoints) {
    const distance = Math.abs(position - point);
    if (distance < minDistance) {
      closestPoint = point;
      minDistance = distance;
    }
  }

  return closestPoint;
};

/**
 * Apply snapping to range values, respecting minimum and maximum interval constraints
 *
 * @param range - Current range as [start, end] percentages
 * @param newStart - New start position
 * @param newEnd - New end position
 * @param periods - Array of period objects
 * @param isFootageVideo - Whether this is footage video (with max interval constraint)
 * @returns Adjusted range with snapping applied
 */
const applySnappingToRange = (
  range: [number, number] | null,
  newStart: number,
  newEnd: number,
  periods: Array<{ trip: { startTimestamp: number; endTimestamp: number } }>,
  isFootageVideo: boolean
): [number, number] => {
  // If no current range, handle initial selection
  if (!range) {
    const snappedStart = snapToNearestPeriod(newStart, periods);
    const snappedEnd = snapToNearestPeriod(newEnd, periods);

    // Apply standard constraints after snapping
    return handleInitialRangeSelection(snappedStart, snappedEnd, isFootageVideo);
  }

  const [currentStart, currentEnd] = range;

  // Determine which thumb is being dragged by comparing changes
  const isDraggingLeftThumb = Math.abs(newStart - currentStart) > Math.abs(newEnd - currentEnd);

  // Apply snapping only to the thumb being dragged
  const snappedStart = isDraggingLeftThumb ? snapToNearestPeriod(newStart, periods) : newStart;
  const snappedEnd = isDraggingLeftThumb ? newEnd : snapToNearestPeriod(newEnd, periods);

  // Apply standard constraints after snapping
  return handleDraggedRange(snappedStart, snappedEnd, currentStart, currentEnd, isFootageVideo);
};

/**
 * Handles the initial range selection and applies constraints
 */
const handleInitialRangeSelection = (start: number, end: number, isFootageVideo: boolean): [number, number] => {
  const interval = end - start;
  const center = (start + end) / 2;

  // Apply minimum interval constraint
  if (interval < MIN_INTERVAL_PERCENT) {
    return [center - MIN_INTERVAL_PERCENT / 2, center + MIN_INTERVAL_PERCENT / 2];
  }

  // Apply maximum interval constraint
  if (isFootageVideo && interval > MAX_INTERVAL_PERCENT) {
    return [center - MAX_INTERVAL_PERCENT / 2, center + MAX_INTERVAL_PERCENT / 2];
  }

  return [start, end];
};

/**
 * Handles range adjustments during drag operations
 */
const handleDraggedRange = (newStart: number, newEnd: number, currentStart: number, currentEnd: number, isFootageVideo: boolean): [number, number] => {
  // Determine which thumb is being dragged
  const isDraggingLeftThumb = Math.abs(newStart - currentStart) > Math.abs(newEnd - currentEnd);

  if (isDraggingLeftThumb) {
    // Adjusting the start (left) thumb
    const constrainedStart = Math.min(newStart, currentEnd - MIN_INTERVAL_PERCENT);

    const finalStart = isFootageVideo && currentEnd - constrainedStart > MAX_INTERVAL_PERCENT ? currentEnd - MAX_INTERVAL_PERCENT : constrainedStart;

    return [finalStart, currentEnd];
  }

  // Adjusting the end (right) thumb
  const constrainedEnd = Math.max(newEnd, currentStart + MIN_INTERVAL_PERCENT);

  const finalEnd = isFootageVideo && constrainedEnd - currentStart > MAX_INTERVAL_PERCENT ? currentStart + MAX_INTERVAL_PERCENT : constrainedEnd;

  return [currentStart, finalEnd];
};

// Export constants
export { MIN_INTERVAL_PERCENT, MAX_INTERVAL_PERCENT, SECONDS_IN_DAY, SNAP_THRESHOLD };

// Export utility functions
export { percentToDateTime, dateTimeToPercent, formatters, getPositionInViewport, calculateDurationFromRange, snapToNearestPeriod, applySnappingToRange };

// Export types
export type { TimeMarker, MarkerSpecs, ViewWindow };
